import { FormProvider, useForm } from 'react-hook-form';
import type { CampaignTypeKey } from 'entities/campaign';
import { useSubmitCampaign, useUpdateCampaignWizard, useGetCampaignSettings } from 'entities/campaign';
import { BudgetInput } from 'shared/ui/Input';
import { Button } from 'shared/ui/Button';
import { FormErrors } from 'shared/ui/FormErrors';
import { isAxiosResponseError } from 'shared/lib/api';
import { formatPrice, getValueFromString } from 'shared/lib/formatting';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { useHidable } from 'shared/lib/hooks';
import { showDefaultErrorToast } from 'shared/lib/notifications';
import { useGetContractor } from 'entities/profile';
import { getBudgetChangeMetricsData, pushToDataLayer } from 'shared/lib/gtm';
import { centsToDollars } from 'shared/lib/format';

import { useCampaignId, useCampaignName, usePreSelectedType } from './CreateCampaignStoreHook';
import { ConfirmationModal } from './ConfirmationModal';

type StepBudgetProps = {
  onBack: () => void;
  onCreate: () => void;
};

type FormType = {
  budget: string;
  forceSubmit?: boolean;
};

const INPUT_NAME = 'budget';
const CONFIRMATION_MODAL_RATIO = 0.4;

export function StepBudget(props: StepBudgetProps) {
  const { onBack, onCreate } = props;
  const confirmationModalState = useHidable(false);
  const campaignId = useCampaignId();
  const updateCampaignQuery = useUpdateCampaignWizard(campaignId);
  const submitCampaignQuery = useSubmitCampaign(campaignId);
  const getCampaignSettings = useGetCampaignSettings(campaignId);
  const minBudget = Number(getCampaignSettings.data?.data.validationRules.campaignsMinimalBudgetValue.amount ?? 0);

  const methods = useForm<FormType>({ defaultValues: { budget: '' } });
  const {
    formState: { errors },
    watch,
    handleSubmit,
  } = methods;
  const inputValue = watch(INPUT_NAME);
  const hasError = Boolean(errors[INPUT_NAME]);
  const isBtnDisabled = hasError || !inputValue || inputValue === '$';
  const userData = useGetContractor().data?.data;
  const campaignType = usePreSelectedType() as CampaignTypeKey;
  const campaignName = useCampaignName();

  const metricsToPush = getBudgetChangeMetricsData({
    contractorId: userData?.id as number,
    contractorEmail: userData?.email as string,
    campaignType,
    campaignId,
    campaignName,
    oldBudget: 0,
    newBudget: getValueFromString(watch().budget),
  });

  const onConfirm = async (budget: string) => {
    confirmationModalState.hide();
    updateCampaignQuery.reset();
    submitCampaignQuery.reset();

    const formattedBudget = getValueFromString(budget);

    try {
      await updateCampaignQuery.mutateAsync({ payload: { budget: formattedBudget } });
      await submitCampaignQuery.mutateAsync();
      onCreate();

      pushToDataLayer({
        event: 'confirm_budget',
        ...metricsToPush,
      });
    } catch (e) {
      if (!isAxiosResponseError(e)) {
        showDefaultErrorToast();
      }
    }
  };

  const onSubmit = async ({ budget }: FormType) => {
    const confirmationValue = centsToDollars(minBudget * CONFIRMATION_MODAL_RATIO + minBudget);

    if (getValueFromString(budget) >= confirmationValue) {
      confirmationModalState.show();

      return;
    }

    await onConfirm(budget);
  };

  const onCancelHandler = () => {
    confirmationModalState.hide();

    pushToDataLayer({
      event: 'edit_budget',
      ...metricsToPush,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="text-center text-xl font-medium tracking-normal">Set weekly budget</div>
          <div className="mt-8 w-full">
            <BudgetInput
              name={INPUT_NAME}
              placeholder="Enter your weekly budget"
              isLightPlaceholder
              {...getDataAutoTestAttributes(['input-create-wizard-create'])}
            />
            {!hasError && getCampaignSettings.data && (
              <div className="mx-4 mt-1 text-xs font-normal text-slate-600">
                Min budget{' '}
                {formatPrice(
                  minBudget,
                  getCampaignSettings.data.data.validationRules.campaignsMinimalBudgetValue.currency
                )}
              </div>
            )}
            <div className="mt-8 flex justify-between">
              <Button
                type="button"
                onClick={onBack}
                variant="outline-second"
                {...getDataAutoTestAttributes(['button-create-wizard-back'])}
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={isBtnDisabled}
                loading={updateCampaignQuery.isLoading || submitCampaignQuery.isLoading}
                {...getDataAutoTestAttributes(['button-create-wizard-create'])}
              >
                Create
              </Button>
            </div>
          </div>
        </div>
        <ConfirmationModal
          isOpen={confirmationModalState.isShown}
          price={inputValue}
          onConfirm={() => onConfirm(inputValue)}
          onClose={confirmationModalState.hide}
          onCancel={onCancelHandler}
        />
        <FormErrors error={updateCampaignQuery.error} />
      </form>
    </FormProvider>
  );
}
